// Libraries
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Components
import SmartSelector from "../../InputComponents/SmartSelector";
import { LabeledFormInput } from "../LabeledFormInput";

// Interfaces
import { ISmartSelectorProps } from "../../InputComponents/SmartSelector/interfaces";

// Constants
import { CRM_KEYS } from "@/DataPages/components/CRM/constants";

function isValidSelection(value: ICountry | null) {
	return Boolean(value?.name && value?.code);
}

async function getCountries() {
	const query = gql`
		query GetCountries {
			getCountries {
				id
				name
				code
			}
		}
	`;

	const variables = {};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getCountries ?? [];
	} catch (error) {
		throw new Error("Failed to fetch countries");
	}
}

type TCountrySelectorProps = {
	inferredCountry?: ICountry | null;
	selectorProps?: Partial<ISmartSelectorProps>;
	required?: boolean;
	disabled: boolean;
	loading: boolean;
	onCountrySelectedAction?: (country: ICountry | null) => void;
	onUpdateCountryId?: (id: number | undefined) => void;
};

export function CountrySelector(props: Readonly<TCountrySelectorProps>) {
	const {
		onUpdateCountryId,
		onCountrySelectedAction,
		inferredCountry = null,
		selectorProps,
		required = false,
		disabled,
		loading,
	} = props;
	const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);

	const {
		data: countries = [],
		isLoading: isLoadingCountries,
		error: errorLoadingCountries,
	} = useQuery<ICountry[]>({
		queryKey: [CRM_KEYS.QUERY.COUNTRY],
		queryFn: async () => getCountries(),
		staleTime: Infinity,
		enabled: true,
	});

	const handleCountrySelection: ISmartSelectorProps["onChangeAction"] = (value) => {
		const country = value as ICountry;
		setSelectedCountry(country);
		onCountrySelectedAction?.(country);
	};

	useEffect(() => {
		if (!selectedCountry && inferredCountry) {
			const country = countries.find((country: ICountry) => country.code === inferredCountry?.code) ?? null;
			setSelectedCountry(country);
			onUpdateCountryId?.(country?.id);
		}
	}, [inferredCountry, countries]);

	return (
		<LabeledFormInput
			label="Country"
			formInput={
				<SmartSelector
					selectBy={"code"}
					showBy={"name"}
					options={countries}
					selected={selectedCountry}
					listBoxProps={{}}
					disabled={disabled}
					sx={{}}
					popperProps={selectorProps?.popperProps}
					onChangeAction={handleCountrySelection}
					loading={isLoadingCountries || loading}
					error={required && (!isValidSelection(selectedCountry) || Boolean(errorLoadingCountries))}
					clearable
					inputProps={{
						placeholder: "Country",
					}}
				/>
			}
		/>
	);
}

export default CountrySelector;
